"use client";

import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Card, Button, Typography, Space, InputNumber } from "antd";
import toast from "react-hot-toast";

import { useWebInfoDetail, useWebInfoSave } from "../../../hooks/api/web-info/hooks";

const { Text } = Typography;

interface IPaymentForm {
  yearly_tax_price: number;
  quarterly_tax_price: number;
}

const schema = yup.object({
  yearly_tax_price:    yup.number().required("Yearly price is required").min(1, "Must be greater than 0"),
  quarterly_tax_price: yup.number().required("Quarterly price is required").min(1, "Must be greater than 0"),
});

const PaymentRangeForm = () => {
  const { data: details } = useWebInfoDetail("PAYMENTS");

  const { mutate: saveForm, isPending } = useWebInfoSave({
    onSuccess: () => toast.success("Payment range saved successfully"),
    onError: (error: any) => toast.error(error?.message || "Something went wrong"),
  });

  const { control, handleSubmit, reset, formState: { errors } } = useForm<IPaymentForm>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      yearly_tax_price:    0,
      quarterly_tax_price: 0,
    },
  });

  useEffect(() => {
    if (details?.data) {
      reset({
        yearly_tax_price:    Number(details?.data?.yearly_tax_price)   ,
        quarterly_tax_price: Number(details?.data?.quarterly_tax_price),
      });
    }
  }, [details, reset]);

  const onSubmit = (values: IPaymentForm) => {
    saveForm({
      ...values,
      type: "PAYMENTS",
      ...(details?.data?._id && { _id: details.data._id }),
    });
  };

  return (
    <div className="space-y-6 p-4">

      {/* YEARLY */}
      <Card style={{ background: "#FFF6EE" }}>
        <Text strong style={{ color: "#EA8A40" }}>Yearly Tax Price</Text>
        <hr className="mt-2 mb-4 border-orange-300 opacity-60" />
        <Controller
          control={control}
          name="yearly_tax_price"
          render={({ field }) => (
            <div className="flex flex-col gap-1">
              <InputNumber
                {...field}
                type="number"
                prefix="£"
                placeholder="Enter yearly price"
                min={1}
                className="w-full"
                size="large"
                style={{ borderColor: "#EA8A40" }}
              />
              {errors?.yearly_tax_price && (
                <span className="text-red-500 text-xs">{errors.yearly_tax_price.message}</span>
              )}
            </div>
          )}
        />
      </Card>

      {/* QUARTERLY */}
      <Card style={{ background: "#FFF6EE" }}>
        <Text strong style={{ color: "#EA8A40" }}>Quarterly Tax Price</Text>
        <hr className="mt-2 mb-4 border-orange-300 opacity-60" />
        <Controller
          control={control}
          name="quarterly_tax_price"
          render={({ field }) => (
            <div className="flex flex-col gap-1">
              <InputNumber
                {...field}
                type="number"
                prefix="£"
                placeholder="Enter quarterly price"
                min={1}
                className="w-full"
                size="large"
                style={{ borderColor: "#EA8A40" }}
              />
              {errors?.quarterly_tax_price && (
                <span className="text-red-500 text-xs">{errors.quarterly_tax_price.message}</span>
              )}
            </div>
          )}
        />
      </Card>

      <Space>
        <Button
          type="primary"
          className="md:px-10"
          loading={isPending}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </Space>
    </div>
  );
};

export default PaymentRangeForm;